import 'reactflow/dist/style.css';
import '../FlowBuilder/FlowBuilder.scss';

import {
  FlowBuilderContext,
  RenameModalData,
  TAudienceSlideoutProps,
  TContentSlideoutProps,
  TEmailSlideoutProps,
  TPushSlideoutProps,
  TSMSSlideoutProps,
  TWebhookSlideoutProps,
} from '../FlowBuilder/context/flow-builder.context';
import React, { useEffect, useMemo, useState } from 'react';
import { mapBranchedJourney, mapLinearJourney } from 'pages/FlowBuilder/helpers/flow-builder.helpers';
import { getFlowBuilderPath } from 'pages/FlowBuilder/helpers/flow-builder.routes';
import { Node, Edge, ReactFlowProvider } from 'reactflow';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import ContentManager from 'components/ExperienceCanvas/ContentManager/ContentManager';
import ErrorSplash from 'components/ExperienceCanvas/ErrorSplash/ErrorSplash';
import { FlowBuilderRouteParams } from '../FlowBuilder/types/flow-builder.types';
import FlowCanvas from 'components/FlowBuilder/FlowCanvas/FlowCanvas';
import FlowHeaderV2, { useExperienceNameValidation } from 'components/FlowBuilderV2/FlowHeaderV2';
import JourneyLaunchPopover from 'components/FlowBuilderV2/JourneyLaunchPopover';
import useJourneyActions from 'components/FlowBuilderV2/hooks/useJourneyActions';
import IconContentFlow from 'pages/FlowBuilder/assets/icons/IconContentFlow';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import PushSidePanel from 'components/FlowBuilder/FlowSidePanels/PushSidePanel/PushSidePanel';
import RenameNodeModal from 'components/FlowBuilder/FlowModals/RenameNodeModal/RenameNodeModal';
import ScheduleSidePanel from 'components/FlowBuilder/FlowSidePanels/ScheduleSidePanel/ScheduleSidePanel';
import TargetAudienceSidePanel from 'components/FlowBuilder/FlowSidePanels/TargetAudienceSidePanel/TargetAudienceSidePanel';
import WebhookSidePanel from 'components/FlowBuilder/FlowSidePanels/WebhookSidePanel/WebhookSidePanel';
import { selectHasEmailNodes } from 'store/email/email.selector';
import { selectHasSMSNodes } from 'store/sms/sms.selector';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import useContentCreationManager from 'hooks/useContentCreationManager';
import { useFeatureFlags } from 'feature-flags';
import useJourneyInstance from 'hooks/useJourneyInstance';
import useSettings from 'hooks/useSetting';
import { WEBHOOK_INIT } from 'constants/webhook.constants';
import { ContextDataSlideOutProvider } from 'components/ExperienceCanvas/ContextDataSlideOut/ContextDataSlideOutContext';
import { SMS_INIT } from 'constants/sms.constants';
import { EMAIL_INIT } from 'constants/email.constants';
import EmailSidePanel from 'components/FlowBuilder/FlowSidePanels/EmailSidePanel/EmailSidePanel';
import SMSSidePanel from 'components/FlowBuilder/FlowSidePanels/SMSSidePanel/SMSSidePanel';
import UsedByModal from 'components/Modal/UsedByModal/UsedByModal';
import { TIsBeingUsedProps } from 'pages/FlowBuilder/context/flow-builder.context';
import { JourneyState } from 'store/journey/journey.type';

const MAIN_CLASS = 'flow-builder';
const LOADING_ICON_CONTAINER = 'loading-icon-container';
const CONTENT_SIDE_PANEL = 'content-side-panel';
const CLASSES = {
  FLOW_CONTAINER: `${MAIN_CLASS}__flow-container`,
  LOADING_ICON: `${LOADING_ICON_CONTAINER}__icon`,
  MODALS: `${MAIN_CLASS}__modals`,
  SIDE_PANELS: `${MAIN_CLASS}__side-panels`,
};

const defaultContentSlideoutProps = {
  isOpen: false,
  headerInfo: {
    goBackTitle: '',
    goBackIcon: <IconContentFlow />,
    goBackSubTitle: '',
    mainTitle: '',
  },
};

function FlowBuilderV2() {
  const [audienceContext, setAudienceContext] = useState<TAudienceSlideoutProps>({
    isOpen: false,
    isViewOnlyMode: false,
  });
  const [isScheduleSidePanelOpen, setScheduleSidePanelOpen] = useState(false);
  const [pushContext, setPushContext] = useState<TPushSlideoutProps>({ isOpen: false });
  const [contentContext, setContentContext] = useState<TContentSlideoutProps>(defaultContentSlideoutProps);
  const [webhookContext, setWebhookContext] = useState<TWebhookSlideoutProps>({ isOpen: false, webhook: WEBHOOK_INIT });
  const [smsContext, setSMSContext] = useState<TSMSSlideoutProps>({ isOpen: false, sms: SMS_INIT });
  const [emailContext, setEmailContext] = useState<TEmailSlideoutProps>({ isOpen: false, email: EMAIL_INIT });
  const [isBeingUsedModalData, setIsBeingUsedModalData] = useState<TIsBeingUsedProps | null>(null);
  const [renameModalData, setRenameModalData] = useState<RenameModalData | null>(null);
  const [stepIndex, setStepIndex] = useState(-1);
  const flags = useFeatureFlags(['tx_flow_multiple_contents', 'tx_flow_layered_layout']);
  const isMultipleContentsEnabled = flags['tx_flow_multiple_contents'];
  const isLayeredLayoutEnabled = flags['tx_flow_layered_layout'];
  const { languages } = useSettings();
  const { pid: projectId, id: journeyId } = useRouteMatch<FlowBuilderRouteParams>().params;
  const defaultLanguage = getDefaultLanguage(languages);
  const history = useHistory();
  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isTemplate =
    urlParams.get('fromTemplateLibrary')?.toLowerCase() === 'true' || urlParams.get('mode') === 'create';
  const createFromId = urlParams.get('createFromId') ?? '';
  const isViewTouchpoint = urlParams.get('viewTouchpoint')?.toLowerCase() === 'true';
  const parentEntityId = urlParams.get('parentEntityId');
  const parentEntityType = urlParams.get('parentEntityType');

  const requiredFlags = useMemo(
    () => ({
      tx_flow_multiple_contents: isMultipleContentsEnabled,
      tx_flow_layered_layout: isLayeredLayoutEnabled,
    }),
    [isMultipleContentsEnabled, isLayeredLayoutEnabled],
  );

  const {
    journeyInstance,
    isLoading,
    fetch: refetch,
    error: journeyError,
  } = useJourneyInstance(journeyId, isTemplate, createFromId, parentEntityId ?? '', parentEntityType ?? '', projectId);

  useContentCreationManager(true);

  const flowBuilderContextValue = {
    shellVariant: 'modal' as const,
    modals: {
      rename: {
        data: renameModalData,
        setData: setRenameModalData,
      },
      isBeingUsed: {
        data: isBeingUsedModalData,
        setData: setIsBeingUsedModalData,
      },
    },
    sidePanels: {
      targetAudience: {
        ...audienceContext,
        setAudienceContext,
        setStepIndex,
        stepIndex,
      },
      schedule: {
        isOpen: isScheduleSidePanelOpen,
        setIsOpen: setScheduleSidePanelOpen,
        setStepIndex,
        stepIndex,
      },
      push: {
        ...pushContext,
        setPushContext,
        setStepIndex,
        stepIndex,
      },
      content: {
        ...contentContext,
        setContentContext,
        setStepIndex,
        stepIndex,
      },
      webhook: {
        ...webhookContext,
        stepIndex,
        setWebhookContext,
        setStepIndex,
      },
      sms: {
        ...smsContext,
        setSMSContext,
        setStepIndex,
        stepIndex,
      },
      email: {
        ...emailContext,
        setEmailContext,
        setStepIndex,
        stepIndex,
      },
    },
  };

  const {
    journeyState,
    ruleState,
    pushState,
    contentState,
    webhookState,
    smsState,
    emailState,
    dependencyGraph,
    hasSMSNodes,
    hasEmailNodes,
  } = useSelector((state) => ({
    journeyState: state.te.journey,
    ruleState: state.te.rule,
    pushState: state.te.push,
    contentState: state.te.content,
    webhookState: state.te.webhook,
    smsState: state.te.sms,
    emailState: state.te.email,
    dependencyGraph: state.te.journey.dependencyGraph,
    hasSMSNodes: selectHasSMSNodes(state.te),
    hasEmailNodes: selectHasEmailNodes(state.te),
  }));

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  const isFlowTemplate = useMemo(
    () => journeyState.steps?.some((step) => !!step.children && step.children.length > 0) ?? false,
    [journeyState.steps],
  );

  const closeUsedByModal = () => {
    flowBuilderContextValue.modals.isBeingUsed.setData({
      isOpen: false,
      referencedBy: [],
      primaryAction: undefined,
      secondaryAction: undefined,
    });
  };

  useEffect(() => {
    if (!!journeyInstance.id && journeyId !== journeyInstance.id) {
      const params = new URLSearchParams(window.location.search);
      params.set('fromTemplateLibrary', 'false');
      history.replace(getFlowBuilderPath(projectId, journeyInstance.id, { v2: true, search: params.toString() }));
    }
  }, [history, projectId, journeyId, journeyInstance.id]);

  useEffect(() => {
    if (!isViewTouchpoint) return;

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('createFromId');
    history.push(`/project/${projectId}/experiences/${journeyId}/overview?${searchParams.toString()}`);
  }, [history, isViewTouchpoint, journeyId, projectId]);

  useEffect(() => {
    const steps = journeyState.steps;
    if (!steps?.length || isViewTouchpoint) return;

    let cancelled = false;

    (async () => {
      const { nodes: mappedNodes, edges: mappedEdges } =
        isFlowTemplate && steps[0].id
          ? await mapBranchedJourney(
              journeyState,
              ruleState,
              pushState,
              contentState,
              webhookState,
              smsState,
              emailState,
              defaultLanguage,
              dependencyGraph,
              requiredFlags,
            )
          : await mapLinearJourney(
              journeyState,
              ruleState,
              pushState,
              contentState,
              webhookState,
              smsState,
              emailState,
              defaultLanguage,
              dependencyGraph,
              requiredFlags,
            );

      if (cancelled) return;

      setNodes(mappedNodes);
      setEdges(mappedEdges as Edge[]);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    contentState,
    defaultLanguage,
    dependencyGraph,
    isFlowTemplate,
    isViewTouchpoint,
    journeyState,
    pushState,
    ruleState,
    webhookState,
    smsState,
    emailState,
    requiredFlags,
  ]);

  return isLoading ? (
    <div className={LOADING_ICON_CONTAINER} aria-label="experience is loading">
      <LoadingIcon className={CLASSES.LOADING_ICON} />
    </div>
  ) : journeyError ? (
    <ErrorSplash error={journeyError} />
  ) : (
    <FlowBuilderContext.Provider value={flowBuilderContextValue}>
      <ContextDataSlideOutProvider>
        <div className={MAIN_CLASS}>
          <FlowCanvasWithControls journeyInstance={journeyInstance} refetch={refetch} nodes={nodes} edges={edges} />
        </div>
        <div className={CLASSES.MODALS}>
          <RenameNodeModal />
          <UsedByModal
            isOpen={flowBuilderContextValue.modals.isBeingUsed.data?.isOpen || false}
            referencedBy={flowBuilderContextValue.modals.isBeingUsed.data?.referencedBy || []}
            closeModal={closeUsedByModal}
            primaryAction={{
              label: 'Cancel',
              onClickHandler: closeUsedByModal,
            }}
            secondaryAction={{
              label: 'Continue',
              onClickHandler: () => {
                flowBuilderContextValue.modals.isBeingUsed.data?.primaryAction?.();
                closeUsedByModal();
              },
            }}
          />
        </div>
        <div className={CLASSES.SIDE_PANELS}>
          <TargetAudienceSidePanel />
          <ScheduleSidePanel />
          <PushSidePanel />
          <ContentManager
            {...contentContext}
            stepIdx={stepIndex}
            onClickClose={() => {
              setStepIndex(-1);
              setContentContext(defaultContentSlideoutProps);
            }}
            headerInfo={contentContext.headerInfo}
            className={CONTENT_SIDE_PANEL}
          />
          <WebhookSidePanel />
          {hasSMSNodes && <SMSSidePanel />}
          {hasEmailNodes && <EmailSidePanel />}
        </div>
      </ContextDataSlideOutProvider>
    </FlowBuilderContext.Provider>
  );
}

type FlowCanvasWithControlsProps = {
  journeyInstance: JourneyState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch: (showLoading?: any) => Promise<void>;
  nodes: Node[];
  edges: Edge[];
};

/**
 * Renders the v2 header, canvas, and floating launch popover. Lives inside
 * `ContextDataSlideOutProvider` so the single lifted `useJourneyActions` instance can reach the
 * context-files slideout. A single action-hook instance is shared by the header (Save/Exit) and the
 * popover (Launch/Deactivate) to avoid duplicate confirm-modal sessions and double file-polling.
 */
function FlowCanvasWithControls({ journeyInstance, refetch, nodes, edges }: FlowCanvasWithControlsProps) {
  const nameValidation = useExperienceNameValidation(journeyInstance);
  const actions = useJourneyActions({
    journeyInstance,
    refetch,
    experienceNameError: nameValidation.experienceNameError,
  });

  return (
    <>
      <FlowHeaderV2 journeyInstance={journeyInstance} actions={actions} nameValidation={nameValidation} />
      <div className={CLASSES.FLOW_CONTAINER}>
        <FlowCanvas nodes={nodes} edges={edges} />
        <JourneyLaunchPopover actions={actions} nameValidation={nameValidation} />
      </div>
    </>
  );
}

function FlowBuilderV2WithProvider() {
  return (
    <ReactFlowProvider>
      <FlowBuilderV2 />
    </ReactFlowProvider>
  );
}

export default FlowBuilderV2WithProvider;
